import React from 'react';
import debounce from 'debounce-promise';
import type { GroupBase, OptionsOrGroups, StylesConfig } from 'react-select';
import type ReactSelect from 'react-select';
import type { AsyncProps } from 'react-select/async';
import type { AsyncCreatableProps } from 'react-select/async-creatable';
import type { CreatableProps } from 'react-select/creatable';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { commonProps, customStyles } from './CustomComponents.tsx';
import { OptionMarkedLabel } from './OptionMarkedLabel.tsx';
import { SAsyncCreatableSelect, StyledSelect, SCreatableSelect, SAsyncSelect } from './styled.ts';
import type { PropsWithError, Option } from './types.ts';
import { useFixedOptions } from './useFixedOptions.ts';
import { useMergeComponents } from './useMergeComponents.ts';

export { default as TagInput } from './TagInput.tsx';

type ForbidIsMulti<T extends { isMulti?: unknown }> = Omit<T, 'isMulti'>;

type SelectProps<OT extends Option> = ForbidIsMulti<
  React.ComponentProps<typeof ReactSelect<OT, false>> &
    PropsWithError & {
      innerRef?: React.Ref<typeof ReactSelect<OT, false>>;
    }
>;

const mergeStyles = <TOption extends Option, TMulti extends boolean>(
  styles: StylesConfig<TOption, TMulti> | undefined,
): StylesConfig<TOption, TMulti> => ({
  ...(customStyles as unknown as StylesConfig<TOption, TMulti>),
  ...styles,
});

// `debounce-promise` wraps the return type in an extra `Promise<>`, which no longer matches
// react-select's public `loadOptions` signature under TS7. Cast it back to the portable type.
type LoadOptionsFn<OT extends Option> = (
  inputValue: string,
  callback: (options: OptionsOrGroups<OT, GroupBase<OT>>) => void,
) => Promise<OptionsOrGroups<OT, GroupBase<OT>>> | void;

const debounceLoadOptions = <OT extends Option>(loadOptions: LoadOptionsFn<OT> | undefined): LoadOptionsFn<OT> | undefined =>
  loadOptions ? (debounce(loadOptions, 300) as unknown as LoadOptionsFn<OT>) : undefined;

// the `ref` should be passed using `innerRef` custom prop & not with `forwardRef` because typescript
// currently doesn't support hoc generic types inference well. therefore it's not possible to create a
// generic component using `forwardRef`
//
// const customFilter is used to support EN/DE localization
export const Select = <OT extends Option>({
  components,
  innerRef,
  formatOptionLabel,
  styles,
  ...props
}: SelectProps<OT>): React.ReactElement => {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-restricted-syntax
  const customFilter = React.useCallback(
    (option: OT, searchText: string) => {
      const target =
        formatOptionLabel?.(option, {
          context: 'value',
          inputValue: '',
          selectValue: [option],
        })?.toString() ?? option.label;
      if (typeof target !== 'string') {
        // eslint-disable-next-line no-console
        console.error('Ether value or label be be string');
        return false;
      }

      return target.toLowerCase().includes(searchText.toLowerCase());
    },
    [formatOptionLabel],
  );

  const mergedComponents = useMergeComponents<OT, false>(components);
  const mergedStyles = React.useMemo(() => mergeStyles<OT, false>(styles), [styles]);

  return (
    <StyledSelect<OT, false>
      styles={mergedStyles}
      filterOption={(option: { data: OT }, inputValue: string) => customFilter(option.data, inputValue)}
      ref={innerRef}
      formatOptionLabel={formatOptionLabel}
      {...commonProps}
      {...props}
      components={mergedComponents}
      data-testid='styled-select'
    />
  );
};

type StyledAsyncCreatableSelectProps<OT extends Option> = ForbidIsMulti<
  AsyncCreatableProps<OT, false, GroupBase<OT>> &
    PropsWithError & {
      innerRef?: React.Ref<typeof ReactSelect<OT, false>>;
    }
>;

export const StyledAsyncCreatableSelect = <OT extends Option>({
  components,
  innerRef,
  loadOptions,
  styles,
  ...props
}: StyledAsyncCreatableSelectProps<OT>): React.ReactElement => {
  const mergedComponents = useMergeComponents<OT, false>(components);
  const mergedStyles = React.useMemo(() => mergeStyles<OT, false>(styles), [styles]);

  return (
    <SAsyncCreatableSelect<OT, false>
      styles={mergedStyles}
      ref={innerRef}
      loadOptions={React.useMemo(() => debounceLoadOptions<OT>(loadOptions), [loadOptions])}
      {...commonProps}
      {...props}
      components={mergedComponents}
    />
  );
};

type AsyncCreatableMultiSelectProps<OT extends Option> = ForbidIsMulti<
  AsyncCreatableProps<OT, true, GroupBase<OT>> &
    PropsWithError & {
      innerRef?: React.Ref<typeof ReactSelect<OT, true>>;
    }
>;

export const AsyncCreatableMultiSelect = <OT extends Option>({
  components,
  innerRef,
  loadOptions,
  styles,
  ...props
}: AsyncCreatableMultiSelectProps<OT>): React.ReactElement => {
  const mergedComponents = useMergeComponents<OT, true>(components);
  const mergedStyles = React.useMemo(() => mergeStyles<OT, true>(styles), [styles]);

  return (
    <SAsyncCreatableSelect<OT, true>
      styles={mergedStyles}
      ref={innerRef}
      loadOptions={React.useMemo(() => debounceLoadOptions<OT>(loadOptions), [loadOptions])}
      {...commonProps}
      {...props}
      isMulti
      components={mergedComponents}
    />
  );
};

type StyledCreatableSelectProps<OT extends Option> = ForbidIsMulti<
  CreatableProps<OT, false, GroupBase<OT>> &
    PropsWithError & {
      innerRef?: React.Ref<typeof ReactSelect<OT, false>>;
    }
>;
export const StyledCreatableSelect = <OT extends Option>({
  components,
  innerRef,
  ...props
}: StyledCreatableSelectProps<OT>): React.ReactElement => {
  const mergedComponents = useMergeComponents<OT, false>(components);

  return (
    <SCreatableSelect<OT, false>
      styles={customStyles as unknown as StylesConfig<OT, false, GroupBase<OT>>}
      ref={innerRef}
      {...commonProps}
      {...props}
      components={mergedComponents}
    />
  );
};

type CreatableMultiSelectProps<OT extends Option> = ForbidIsMulti<
  CreatableProps<OT, true, GroupBase<OT>> &
    PropsWithError & {
      innerRef?: React.Ref<typeof ReactSelect<OT, true>>;
    }
>;
export const CreatableMultiSelect = <OT extends Option>({
  components,
  innerRef,
  ...restProps
}: CreatableMultiSelectProps<OT>): React.ReactElement => {
  const mergedComponents = useMergeComponents<OT, true>(components);
  const fixed = useFixedOptions(restProps);

  return (
    <SCreatableSelect<OT, true>
      styles={customStyles as unknown as StylesConfig<OT, true, GroupBase<OT>>}
      ref={innerRef}
      {...commonProps}
      {...restProps}
      {...fixed}
      isMulti
      components={mergedComponents}
    />
  );
};

export type AsyncSelectProps<OT extends Option> = ForbidIsMulti<
  AsyncProps<OT, false, GroupBase<OT>> &
    PropsWithError & {
      innerRef?: React.Ref<typeof ReactSelect<OT, false>>;
    }
>;
export const AsyncSelect = <OT extends Option>({
  components,
  innerRef,
  loadOptions,
  styles,
  ...props
}: AsyncSelectProps<OT>): React.ReactElement => {
  const mergedComponents = useMergeComponents<OT, false>(components);
  const mergedStyles = React.useMemo(() => mergeStyles<OT, false>(styles), [styles]);

  return (
    <SAsyncSelect<OT, false>
      styles={mergedStyles}
      ref={innerRef}
      loadOptions={React.useMemo(() => debounceLoadOptions<OT>(loadOptions), [loadOptions])}
      defaultOptions
      {...commonProps}
      {...props}
      components={mergedComponents}
    />
  );
};

const multiProps = {
  isMulti: true,
  closeMenuOnSelect: false,
};

export type MultiSelectProps<OT extends Option> = ForbidIsMulti<
  React.ComponentProps<typeof ReactSelect<OT, true>> &
    PropsWithError & {
      innerRef?: React.Ref<typeof ReactSelect<OT, true>>;
    }
>;
export const MultiSelect = <OT extends Option>({
  components,
  innerRef,
  formatOptionLabel,
  filterOption,
  ...restProps
}: MultiSelectProps<OT>): React.ReactElement => {
  const mergedComponents = useMergeComponents<OT, true>(components);
  const fixed = useFixedOptions(restProps);

  // Auto-generate customFilter for better search when formatOptionLabel is provided (same pattern as Select component)
  const customFilter = useCallbackRef((option: OT, searchText: string) => {
    if (!formatOptionLabel) return true;

    const target =
      formatOptionLabel(option, {
        context: 'value',
        inputValue: '',
        selectValue: [option],
      })?.toString() ?? option.label;

    if (typeof target !== 'string') {
      return false;
    }

    return target.toLowerCase().includes(searchText.toLowerCase());
  });

  return (
    <StyledSelect<OT, true>
      styles={customStyles as unknown as StylesConfig<OT, true, GroupBase<OT>>}
      ref={innerRef}
      formatOptionLabel={formatOptionLabel}
      filterOption={
        filterOption ||
        (formatOptionLabel ? (option: { data: OT }, inputValue: string) => customFilter(option.data, inputValue) : undefined)
      }
      {...multiProps}
      {...commonProps}
      {...restProps}
      {...fixed}
      components={mergedComponents}
    />
  );
};

export type AsyncMultiSelectProps<OT extends Option> = ForbidIsMulti<
  AsyncProps<OT, true, GroupBase<OT>> &
    PropsWithError & {
      innerRef?: React.Ref<typeof ReactSelect<OT, true>>;
    }
>;
export const AsyncMultiSelect = <OT extends Option>({
  components,
  innerRef,
  loadOptions,
  styles,
  ...props
}: AsyncMultiSelectProps<OT>): React.ReactElement => {
  const mergedComponents = useMergeComponents<OT, true>(components);
  const mergedStyles = React.useMemo(() => mergeStyles<OT, true>(styles), [styles]);

  return (
    <SAsyncSelect<OT, true>
      styles={mergedStyles}
      ref={innerRef}
      loadOptions={React.useMemo(() => debounceLoadOptions<OT>(loadOptions), [loadOptions])}
      defaultOptions
      {...multiProps}
      {...commonProps}
      {...props}
      components={mergedComponents}
    />
  );
};

export { type Option, OptionMarkedLabel };
export { CustomMenuList, CustomOption } from './CustomComponents.tsx';
